// Firestore Service
// Low-level Firestore operations

import {
    collection,
    addDoc,
    onSnapshot,
    query,
    where,
    doc,
    getDoc,
    setDoc,
    updateDoc,
    getDocs,
    serverTimestamp,
    orderBy
} from "firebase/firestore";
import { db } from '../config/firebase-config.js';
import { STATUS } from '../config/constants.js';

/**
 * Queue Collection Operations
 */

// Add a new customer to the queue
export async function addCustomerToQueue(customerData) {
    try {
        const docRef = await addDoc(collection(db, "queue"), {
            ...customerData,
            createdAt: serverTimestamp()
        });
        return docRef.id;
    } catch (error) {
        console.error("Error adding customer to queue:", error);
        throw error;
    }
}

// Get a single queue document by ID
export async function getQueueDocument(customerId) {
    try {
        const docRef = doc(db, "queue", customerId);
        const docSnap = await getDoc(docRef);
        return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
    } catch (error) {
        console.error("Error getting queue document:", error);
        throw error;
    }
}

// Update a queue document
export async function updateQueueDocument(customerId, data) {
    try {
        await updateDoc(doc(db, "queue", customerId), data);
    } catch (error) {
        console.error("Error updating queue document:", error);
        throw error;
    }
}

// Listen to queue changes
export function listenToQueue(callback) {
    const activeStatuses = [
        STATUS.WAITING,
        STATUS.CALLING,
        STATUS.SERVING,
        STATUS.STANDBY,
        STATUS.DONE
    ];

    const q = query(
        collection(db, "queue"),
        where("status", "in", activeStatuses),
        orderBy("isPreferential", "desc"),
        orderBy("createdAt", "asc")
    );

    return onSnapshot(q, (snapshot) => {
        const customers = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));
        callback(customers);
    }, (error) => {
        console.error("Error in queue listener:", error);
    });
}

/**
 * Users Collection Operations
 */

// Get a user document by UID
export async function getUserDocument(uid) {
    try {
        const docRef = doc(db, "users", uid);
        const docSnap = await getDoc(docRef);
        return docSnap.exists() ? docSnap.data() : null;
    } catch (error) {
        console.error("Error getting user document:", error);
        throw error;
    }
}

// Set/update a user document
export async function setUserDocument(uid, data, merge = true) {
    try {
        await setDoc(doc(db, "users", uid), data, { merge });
    } catch (error) {
        console.error("Error setting user document:", error);
        throw error;
    }
}

// Update a user document
export async function updateUserDocument(uid, data) {
    try {
        await updateDoc(doc(db, "users", uid), data);
    } catch (error) {
        console.error("Error updating user document:", error);
        throw error;
    }
}

// Find user by email
export async function findUserByEmail(email) {
    try {
        const q = query(collection(db, "users"), where("email", "==", email));
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
            return null;
        }

        const doc = querySnapshot.docs[0];
        return { id: doc.id, ...doc.data() };
    } catch (error) {
        console.error("Error finding user by email:", error);
        throw error;
    }
}

// Listen to all users (for seller status)
export function listenToUsers(callback) {
    return onSnapshot(collection(db, "users"), (snapshot) => {
        const users = {};
        snapshot.forEach(doc => {
            users[doc.id] = doc.data();
        });
        callback(users);
    }, (error) => {
        console.error("Error in users listener:", error);
    });
}

// Export serverTimestamp for use in other modules
export { serverTimestamp };
